import React from 'react';
import { ClerkProvider, SignedIn, SignedOut } from '@clerk/clerk-react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import SignUp from './pages/SignUp';
import SignIn from './pages/SignIn';
import Dashboard from './pages/Dashboard';
import AppWorkspace, { DefaultWorkspaceRoute } from './pages/AppWorkspace';
import Profile from './pages/Profile';

// Workspace sections
import Home               from './pages/workspace/Home';
import OrganisationSetup  from './pages/workspace/OrganisationSetup';
import Organisation       from './pages/workspace/Organisation';
import Locations          from './pages/workspace/Locations';
import Branches           from './pages/workspace/Branches';
import Departments        from './pages/workspace/Departments';
import Roles              from './pages/workspace/Roles';
import RoleDetail         from './pages/workspace/RoleDetail';
import RolePrintView      from './pages/workspace/RolePrintView';
import Policies           from './pages/workspace/Policies';
import PolicyDetail       from './pages/workspace/PolicyDetail';
import OrgChart           from './pages/workspace/OrgChart';
import Users              from './pages/workspace/Users';
import Tasks              from './pages/workspace/Tasks';
import TaskDetail         from './pages/workspace/TaskDetail';
import Projects           from './pages/workspace/Projects';
import ContractsShell, { ContractsIndex } from './pages/workspace/ContractsShell';
import Clients            from './pages/workspace/contracts/Clients';
import Services           from './pages/workspace/contracts/Services';
import Agreements         from './pages/workspace/contracts/Agreements';
import ComplianceBoard   from './pages/workspace/contracts/ComplianceBoard';
import ContractReports   from './pages/workspace/contracts/ContractReports';
import WorkShell          from './pages/workspace/WorkShell';
import PerformanceHub     from './pages/workspace/PerformanceHub';
import PerformanceReviewCycles from './pages/workspace/PerformanceReviewCycles';
import PerformanceReview  from './pages/workspace/PerformanceReview';
import WorkspaceSettings  from './pages/workspace/WorkspaceSettings';
import Feed               from './pages/workspace/Feed';
import Chat               from './pages/workspace/chat/Chat';
import TaskReports        from './pages/workspace/TaskReports';
import ProjectReports     from './pages/workspace/ProjectReports';
import FinancialReport    from './pages/workspace/FinancialReport';
import PerformanceReport  from './pages/workspace/PerformanceReport';
import GoalsReport        from './pages/workspace/GoalsReport';
import AppraisalReport    from './pages/workspace/AppraisalReport';
import OtherReports       from './pages/workspace/OtherReports';
import MyDashboard        from './pages/workspace/MyDashboard';
import TeamDashboard      from './pages/workspace/TeamDashboard';
import MyRole                from './pages/workspace/MyRole';
import MyOrganisation        from './pages/workspace/MyOrganisation';
import MyHRPolicies          from './pages/workspace/MyHRPolicies';
import MyOrganisationShell   from './pages/workspace/MyOrganisationShell';
import MyRoutines         from './pages/workspace/MyRoutines';
import TeamRoutines       from './pages/workspace/TeamRoutines';
import RoutinesHub        from './pages/workspace/RoutinesHub';
import BusinessReviewHub  from './pages/workspace/BusinessReviewHub';

// Admin
import AdminLayout         from './pages/admin/AdminLayout';
import AdminHome           from './pages/admin/AdminHome';
import ReconciliationPage  from './pages/admin/ReconciliationPage';
import FinancialReports    from './pages/admin/FinancialReports';
import TenantReports       from './pages/admin/TenantReports';
import UsageReports        from './pages/admin/UsageReports';
import PlansPage           from './pages/admin/PlansPage';
import AuditLogPage        from './pages/admin/AuditLogPage';
import UsersPage           from './pages/admin/UsersPage';
import ServerHealth        from './pages/admin/ServerHealth';
import PartnerApplicationsPage from './pages/admin/partners/PartnerApplicationsPage';
import PartnersListPage        from './pages/admin/partners/PartnersListPage';
import PartnerTiersPage        from './pages/admin/partners/PartnerTiersPage';
import PartnerLinksAuditPage   from './pages/admin/partners/PartnerLinksAuditPage';
import PartnerPayoutsPage      from './pages/admin/partners/PartnerPayoutsPage';

// Partner app
import PartnerLayout from './pages/partner/PartnerLayout';
import PartnerHome   from './pages/partner/PartnerHome';
import PartnerClients from './pages/partner/PartnerClients';
import PartnerTier    from './pages/partner/PartnerTier';
import PartnerBilling from './pages/partner/PartnerBilling';

import Landing from './pages/Landing';
import AcceptInvite from './pages/AcceptInvite';
import AnimatedFavicon from './components/AnimatedFavicon';
import { ThemeProvider } from './lib/ThemeContext';
import './styles/theme.css';
import './App.css';

const clerkPublishableKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

if (!clerkPublishableKey) {
  throw new Error('Missing CLERK_PUBLISHABLE_KEY');
}

function App() {
  return (
    <ThemeProvider>
    <AnimatedFavicon />
    <ClerkProvider
      publishableKey={clerkPublishableKey}
      localization={{ userProfile: { dangerSection: { title: 'Account Settings' } } }}
      appearance={{
        variables: {
          colorScheme:          'light',
          colorBackground:      '#ffffff',
          colorInputBackground: '#ffffff',
          colorText:            '#111827',
          colorInputText:       '#111827',
          colorTextSecondary:   '#6b7280',
          colorNeutral:         '#111827',
          colorPrimary:         '#2563eb',
          colorDanger:          '#dc2626',
          colorBorder:          '#d1d5db',
        },
        elements: {
          formFieldInput: {
            backgroundColor: '#ffffff',
            color: '#111827',
            colorScheme: 'light',
            borderColor: '#d1d5db',
          },
        },
      }}
    >
      <BrowserRouter>
        <Routes>
          <Route path="/invite/:token" element={<AcceptInvite />} />
          <Route path="/signup"    element={<SignUp />} />
          <Route path="/signin/*"  element={<SignIn />} />

          <Route
            path="/dashboard"
            element={<><SignedIn><Dashboard /></SignedIn><SignedOut><Navigate to="/signin" replace /></SignedOut></>}
          />

          <Route
            path="/profile"
            element={<><SignedIn><Profile /></SignedIn><SignedOut><Navigate to="/signin" replace /></SignedOut></>}
          />

          {/* Admin (platform-admin only — backend gates with requirePlatformAdmin) */}
          <Route
            path="/admin"
            element={<><SignedIn><AdminLayout /></SignedIn><SignedOut><Navigate to="/signin" replace /></SignedOut></>}
          >
            <Route index                element={<AdminHome />} />
            <Route path="reconciliation" element={<ReconciliationPage />} />
            <Route path="financial"      element={<FinancialReports />} />
            <Route path="tenants"        element={<TenantReports />} />
            <Route path="usage"          element={<UsageReports />} />
            <Route path="plans"          element={<PlansPage />} />
            <Route path="audit"          element={<AuditLogPage />} />
            <Route path="users"          element={<UsersPage />} />
            <Route path="server-health"  element={<ServerHealth />} />
            <Route path="partners"                    element={<PartnersListPage />} />
            <Route path="partners/applications"       element={<PartnerApplicationsPage />} />
            <Route path="partners/tiers"              element={<PartnerTiersPage />} />
            <Route path="partners/client-links"       element={<PartnerLinksAuditPage />} />
            <Route path="partners/payouts"            element={<PartnerPayoutsPage />} />
          </Route>

          {/* Partner (approved-partner only — backend gates with requirePartnerAccount) */}
          <Route
            path="/partner"
            element={<><SignedIn><PartnerLayout /></SignedIn><SignedOut><Navigate to="/signin" replace /></SignedOut></>}
          >
            <Route index          element={<PartnerHome />} />
            <Route path="clients" element={<PartnerClients />} />
            <Route path="tier"    element={<PartnerTier />} />
            <Route path="billing" element={<PartnerBilling />} />
          </Route>

          {/* App Workspace */}
          <Route
            path="/apps/:appId"
            element={<><SignedIn><AppWorkspace /></SignedIn><SignedOut><Navigate to="/signin" replace /></SignedOut></>}
          >
            <Route index element={<DefaultWorkspaceRoute />} />

            {/* Home — tab shell (My Dashboard + Team Dashboard + member tabs) */}
            <Route path="home" element={<Home />}>
              <Route index element={<Navigate to="my" replace />} />
              <Route path="my"              element={<MyDashboard />} />
              <Route path="manager"         element={<TeamDashboard managerMode={true} />} />
              <Route path="team"            element={<TeamDashboard />} />
              <Route path="my-role"         element={<MyRole />} />
              <Route path="my-routines"     element={<MyRoutines />} />
              <Route path="my-organisation" element={<MyOrganisation />} />
              <Route path="org-chart"       element={<OrgChart hideCTC />} />
              <Route path="hr-policies"     element={<MyHRPolicies />} />
            </Route>

            {/* Organisation Setup tabs */}
            <Route path="setup" element={<OrganisationSetup />}>
              <Route index element={<Navigate to="organisation" replace />} />
              <Route path="organisation"  element={<Organisation />} />
              <Route path="locations"     element={<Locations />} />
              <Route path="branches"      element={<Branches />} />
              <Route path="departments"   element={<Departments />} />
              <Route path="roles"         element={<Roles />} />
              <Route path="services"      element={<Services />} />
              <Route path="hr-policies"   element={<Policies />} />
              <Route path="org-chart"     element={<OrgChart />} />
              <Route path="review-cycles" element={<PerformanceReviewCycles />} />
              <Route path="settings"      element={<WorkspaceSettings />} />
            </Route>

            {/* Role detail */}
            <Route path="setup/roles/:roleId"        element={<RoleDetail />} />
            <Route path="setup/roles/:roleId/print"  element={<RolePrintView />} />
            {/* Member-accessible role print view (no setup/ prefix) */}
            <Route path="roles/:roleId/print"        element={<RolePrintView />} />

            {/* My Organisation — member tab shell */}
            <Route path="my-organisation" element={<MyOrganisationShell />}>
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview"     element={<MyOrganisation />} />
              <Route path="my-role"      element={<MyRole />} />
              <Route path="hr-policies"  element={<MyHRPolicies />} />
              <Route path="org-chart"    element={<OrgChart hideCTC />} />
            </Route>

            {/* Policies */}
            <Route path="policies"           element={<Policies />} />
            <Route path="policies/:policyId" element={<PolicyDetail />} />

            {/* Org Chart */}
            <Route path="org-chart" element={<OrgChart />} />

            {/* Users */}
            <Route path="users" element={<Users />} />

            {/* Work shell — Tasks only */}
            <Route path="work" element={<WorkShell />}>
              <Route index element={<Navigate to="tasks" replace />} />
              <Route path="tasks" element={<Tasks />} />
              {/* Legacy redirects for old work/* URLs */}
              <Route path="performance" element={<Navigate to="../../performance" replace />} />
              <Route path="feed"        element={<Navigate to="../../feed"        replace />} />
            </Route>

            {/* Tasks — top-level (sidebar links here) */}
            <Route path="tasks" element={<Tasks />} />

            {/* Task detail */}
            <Route path="tasks/:taskId" element={<TaskDetail />} />

            {/* Projects */}
            <Route path="projects" element={<Projects />} />

            {/* Clients — standalone */}
            <Route path="clients" element={<Clients />} />

            {/* Contracts (OSMS) */}
            <Route path="contracts" element={<ContractsShell />}>
              <Route index element={<ContractsIndex />} />
              <Route path="services"         element={<Services readOnly />} />
              <Route path="agreements"       element={<Agreements />} />
              <Route path="compliance-board" element={<ComplianceBoard />} />
              <Route path="reports"          element={<ContractReports />} />
            </Route>

            {/* Agreements Reports — standalone (no shell tabs) */}
            <Route path="contract-report" element={<ContractReports />} />

            {/* Feed — top-level */}
            <Route path="feed" element={<Feed />} />

            {/* Chat — top-level */}
            <Route path="chat" element={<Chat />} />

            {/* Dashboard compat redirects → home */}
            <Route path="dashboard"                 element={<Navigate to="home"      replace />} />
            <Route path="dashboard/my"              element={<Navigate to="home/my"   replace />} />
            <Route path="dashboard/team"            element={<Navigate to="home/team" replace />} />
            <Route path="dashboard/my-role"         element={<Navigate to="home/my-role"         replace />} />
            <Route path="dashboard/my-organisation" element={<Navigate to="home/my-organisation" replace />} />
            <Route path="dashboard/hr-policies"     element={<Navigate to="home/hr-policies"     replace />} />
            <Route path="dashboard/org-chart"       element={<Navigate to="home/my"   replace />} />

            {/* Performance — top-level */}
            <Route path="performance" element={<PerformanceHub />} />

            {/* Reports */}
            <Route path="task-reports"        element={<TaskReports />} />
            <Route path="project-reports"     element={<ProjectReports />} />
            <Route path="financial-report"    element={<FinancialReport />} />
            <Route path="performance-report"  element={<PerformanceReport />} />
            <Route path="goals-report"        element={<GoalsReport />} />
            <Route path="appraisal-report"    element={<AppraisalReport />} />
            <Route path="other-reports"       element={<OtherReports />} />

            {/* Routines — personal view */}
            <Route path="routines"          element={<RoutinesHub />} />
            {/* Routines — manager/admin team view (Routine Analysis "View All") */}
            <Route path="team-routines"     element={<TeamRoutines />} />
            {/* Business Review Module */}
            <Route path="business-reviews"  element={<BusinessReviewHub />} />
            <Route path="performance/reviews/:reviewId" element={<PerformanceReview />} />

            {/* Compatibility redirects */}
            <Route path="organisation" element={<Navigate to="setup/organisation" replace />} />
            <Route path="locations"    element={<Navigate to="setup/locations"    replace />} />
            <Route path="branches"     element={<Navigate to="setup/branches"     replace />} />
            <Route path="departments"  element={<Navigate to="setup/departments"  replace />} />
            <Route path="roles"        element={<Navigate to="setup/roles"        replace />} />
            <Route path="members"      element={<Navigate to="users"              replace />} />
            <Route path="my-dashboard"   element={<Navigate to="home/my"   replace />} />
            <Route path="team-dashboard" element={<Navigate to="home/team" replace />} />

            {/* Old standalone performance routes → hub */}
            <Route path="performance/goals"      element={<Navigate to="work/performance" replace />} />
            <Route path="performance/my-reviews" element={<Navigate to="work/performance" replace />} />
            <Route path="performance/manager"    element={<Navigate to="work/performance" replace />} />
            <Route path="performance/appraisal"  element={<Navigate to="work/performance" replace />} />
          </Route>

          {/* Root — signed-in users go to dashboard, signed-out go to signup */}
          <Route
            path="/"
            element={<><SignedIn><Navigate to="/dashboard" replace /></SignedIn><SignedOut><Navigate to="/signup" replace /></SignedOut></>}
          />
          <Route
            path="*"
            element={<><SignedIn><Navigate to="/dashboard" replace /></SignedIn><SignedOut><Navigate to="/" replace /></SignedOut></>}
          />
        </Routes>
      </BrowserRouter>
    </ClerkProvider>
    </ThemeProvider>
  );
}

export default App;
